import React, { useState } from 'react';
import { FiX, FiUser, FiCalendar, FiDollarSign, FiMapPin, FiMail, FiExternalLink } from 'react-icons/fi';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import adminProjectService from '../../services/adminProjectService';
import toast from 'react-hot-toast';

const ProjectDetailsModal = ({ project, onClose, onApprove, onReject }) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const formatted = adminProjectService.formatProjectForDisplay(project);

  // Handle reject with reason
  const handleRejectWithReason = async () => {
    if (!rejectionReason || rejectionReason.trim().length < 10) {
      toast.error('Rejection reason must be at least 10 characters long');
      return;
    }

    setLoading(true);
    try {
      await adminProjectService.rejectProject(project.id, rejectionReason);
      toast.success('Project rejected successfully');
      onClose();
      window.location.reload(); // Refresh the projects list
    } catch (error) {
      toast.error(error.message || 'Failed to reject project');
    } finally {
      setLoading(false);
    }
  };

  // Handle approve
  const handleApprove = async () => {
    setLoading(true);
    try {
      await adminProjectService.approveProject(project.id);
      toast.success('Project approved successfully');
      onClose();
      window.location.reload(); // Refresh the projects list
    } catch (error) {
      toast.error(error.message || 'Failed to approve project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Project Review</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Project Header */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Project Media (Video or Image) */}
            <div className="lg:w-1/3">
              {project.video_url ? (
                <div className="w-full h-48 lg:h-64 bg-black rounded-lg overflow-hidden flex items-center justify-center shadow-md">
                  <video
                    src={project.video_url}
                    poster={project.cover_image}
                    controls
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : project.cover_image && (
                <img
                  src={project.cover_image}
                  alt={project.title}
                  className="w-full h-48 lg:h-64 object-cover rounded-lg shadow-md"
                />
              )}
            </div>

            {/* Project Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-2xl font-bold text-gray-900">
                  {project.title || 'Untitled Project'}
                </h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${formatted.statusBadgeColor}`}>
                  {project.status}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <FiUser className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Owner:</span>
                  <span className="font-medium">{formatted.ownerName}</span>
                </div>

                <div className="flex items-center gap-2">
                  <FiDollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Goal:</span>
                  <span className="font-medium">{formatted.formattedFundingGoal}</span>
                </div>

                <div className="flex items-center gap-2">
                  <FiCalendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Submitted:</span>
                  <span className="font-medium">{formatted.submittedDate}</span>
                </div>

                {project.country && (
                  <div className="flex items-center gap-2">
                    <FiMapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Location:</span>
                    <span className="font-medium">{project.country}{project.region && `, ${project.region}`}</span>
                  </div>
                )}

                {project.contact_email && (
                  <div className="flex items-center gap-2">
                    <FiMail className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Contact:</span>
                    <span className="font-medium">{project.contact_email}</span>
                  </div>
                )}

                {project.app_url && (
                  <div className="flex items-center gap-2">
                    <FiExternalLink className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">App URL:</span>
                    <a
                      href={project.app_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary-teal hover:underline"
                    >
                      View App
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Project Story */}
          {project.story && (
            <Card>
              <CardHeader>
                <CardTitle>Project Story</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{project.story}</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Funding Breakdown */}
          {project.funding_use && (
            <Card>
              <CardHeader>
                <CardTitle>Funding Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Array.isArray(project.funding_use) ? project.funding_use.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-gray-700">{item.description || item.category}</span>
                      <span className="font-medium">{formatted.currencySymbol} {parseFloat(item.amount || 0).toLocaleString()}</span>
                    </div>
                  )) : (
                    <p className="text-gray-600">Funding breakdown not provided</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Team Members */}
          {project.team_members && Array.isArray(project.team_members) && project.team_members.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Team Members</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {project.team_members.map((member, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{member.name}</p>
                        {member.email && <p className="text-sm text-gray-600">{member.email}</p>}
                      </div>
                      {member.social_link && (
                        <a
                          href={member.social_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-teal hover:underline text-sm"
                        >
                          View Profile
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Milestones */}
          {project.milestones && Array.isArray(project.milestones) && project.milestones.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Project Milestones</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {project.milestones.map((milestone, index) => (
                    <div key={index} className="border-l-2 border-primary-teal pl-4">
                      <h4 className="font-medium text-gray-900">{milestone.title}</h4>
                      {milestone.date && (
                        <p className="text-sm text-gray-600">Target: {new Date(milestone.date).toLocaleDateString()}</p>
                      )}
                      {milestone.description && (
                        <p className="text-sm text-gray-700 mt-1">{milestone.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risks & Challenges */}
          {project.risks_challenges && (
            <Card>
              <CardHeader>
                <CardTitle>Risks & Challenges</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 whitespace-pre-wrap">{project.risks_challenges}</p>
              </CardContent>
            </Card>
          )}

          {/* Owner Information */}
          <Card>
            <CardHeader>
              <CardTitle>Project Owner Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                {project.owner?.profile_image && (
                  <img
                    src={project.owner.profile_image}
                    alt={project.owner.pi_username}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                )}
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {project.owner?.full_name || project.owner?.pi_username}
                  </h4>
                  <p className="text-sm text-gray-600">@{project.owner?.pi_username}</p>
                  <p className="text-sm text-gray-600">{project.owner?.email}</p>
                  {project.owner?.country && (
                    <p className="text-sm text-gray-600">
                      📍 {project.owner.country}{project.owner.region && `, ${project.owner.region}`}
                    </p>
                  )}
                  <div className="mt-2 text-sm">
                    <span className="text-gray-600">Total Donated: </span>
                    <span className="font-medium">π {parseFloat(project.owner?.total_donated || 0).toLocaleString()}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-gray-600">Member Since: </span>
                    <span className="font-medium">{new Date(project.owner?.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="border-t p-6">
          {!showRejectForm ? (
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                onClick={onClose}
                variant="outline"
                disabled={loading}
              >
                Close
              </Button>
              <Button
                onClick={() => setShowRejectForm(true)}
                variant="outline"
                className="text-red-600 border-red-600 hover:bg-red-50"
                disabled={loading}
              >
                Reject Project
              </Button>
              <Button
                onClick={handleApprove}
                className="bg-green-600 hover:bg-green-700 text-white"
                disabled={loading}
              >
                {loading ? 'Approving...' : 'Approve Project'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason (Required - minimum 10 characters)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejecting this project..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-teal focus:border-transparent"
                  rows={4}
                  maxLength={1000}
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {rejectionReason.length}/1000 characters (minimum 10)
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <Button
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectionReason('');
                  }}
                  variant="outline"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRejectWithReason}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={loading || rejectionReason.trim().length < 10}
                >
                  {loading ? 'Rejecting...' : 'Confirm Rejection'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsModal;
